import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Phone, MessageCircle, Clock, Shield, Palette, Footprints, ChevronDown, Award, Star, Heart, Quote } from 'lucide-react';
import { brandTheme } from '../styles/BrandTheme';
import HeroSectionComponent from '../components/HeroSection';

const HeroSection = styled.section`
  height: 100vh;
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%),
    url('/images/resin-surfaces-bound-work8.jpeg') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
  padding: 0 1rem;

  /* iPad Pro and Large Tablets */
  @media (max-width: 1366px) and (min-width: 1025px) {
    height: 100vh;
    padding: 0 2rem;
  }

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    height: 100vh;
    padding: 0 1.5rem;
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    height: 100vh;
    padding: 0 1rem;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    height: 100vh;
    padding: 0 0.75rem;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    height: 100vh;
    padding: 0 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(254, 206, 27, 0.1) 50%, transparent 70%);
    animation: shimmer 3s ease-in-out infinite;
  }

  @keyframes shimmer {
    0%, 100% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0 0.5rem;
    max-width: 100%;
  }

  @media (max-width: 389px) {
    padding: 0 0.25rem;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2rem, 5.5vw, 4.5rem);
  margin-bottom: 1.5rem;
  font-weight: 700;
  line-height: 1.1;
  background: linear-gradient(135deg, #FECE1B 0%, #ffffff 50%, #FECE1B 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);

  /* Mobile adjustments */
  @media (max-width: 389px) {
    font-size: clamp(1.75rem, 5vw, 2.5rem);
    margin-bottom: 1rem;
    line-height: 1.2;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    font-size: clamp(1.875rem, 5.25vw, 3rem);
    margin-bottom: 1.25rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    font-size: clamp(1.5rem, 4vw, 2.5rem);
    margin-bottom: 0.75rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.5vw, 1.5rem);
  margin-bottom: 2.5rem;
  opacity: 0.95;
  line-height: 1.6;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  /* Mobile adjustments */
  @media (max-width: 389px) {
    font-size: clamp(0.875rem, 2.25vw, 1.125rem);
    margin-bottom: 1.5rem;
    line-height: 1.5;
    max-width: 100%;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    font-size: clamp(0.9375rem, 2.375vw, 1.25rem);
    margin-bottom: 2rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    font-size: clamp(0.75rem, 2vw, 1rem);
    margin-bottom: 1rem;
    line-height: 1.4;
  }
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 389px) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    flex-direction: row;
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

const HeroBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(254, 206, 27, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  padding: 0.75rem 1.5rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: white;

  svg {
    color: #FECE1B;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    margin-bottom: 1.5rem;
    font-size: 0.8rem;
  }

  @media (max-width: 389px) {
    padding: 0.4rem 0.75rem;
    margin-bottom: 1rem;
    font-size: 0.75rem;
    gap: 0.25rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.3rem 0.75rem;
    margin-bottom: 0.75rem;
    font-size: 0.7rem;
  }
`;

const StyledButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  position: relative;
  overflow: hidden;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0.875rem 1.75rem;
    font-size: 0.95rem;
    gap: 0.5rem;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }

  @media (max-width: 389px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.85rem;
    gap: 0.375rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    gap: 0.25rem;
  }

  /* Icon sizing for mobile */
  @media (max-width: 768px) {
    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 389px) {
    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (orientation: landscape) and (max-height: 500px) {
    svg {
      width: 12px;
      height: 12px;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &.primary {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.4);
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(254, 206, 27, 0.5);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-2px);
    }
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #FECE1B;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  padding: 0.75rem 1rem;
  border-radius: 25px;
  border: 1px solid rgba(254, 206, 27, 0.3);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);

  &:hover {
    color: #FECE1B;
    transform: translateX(-50%) translateY(-5px);
    background: rgba(254, 206, 27, 0.1);
    box-shadow: 0 12px 35px rgba(254, 206, 27, 0.2);
  }

  span {
    font-size: 0.85rem;
    font-weight: 600;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  svg {
    filter: drop-shadow(0 0 8px rgba(254, 206, 27, 0.5));
    animation: bounce 2s infinite;
  }

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    bottom: 1.5rem;
    padding: 0.4rem 0.6rem;
    gap: 0.25rem;
    
    span {
      font-size: 0.75rem;
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 427px) and (min-width: 390px) {
    bottom: 1.25rem;
    padding: 0.35rem 0.5rem;
    
    span {
      font-size: 0.7rem;
    }
    
    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (max-width: 389px) {
    bottom: 1rem;
    padding: 0.3rem 0.45rem;
    gap: 0.2rem;
    
    span {
      font-size: 0.65rem;
    }
    
    svg {
      width: 12px;
      height: 12px;
    }
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    bottom: 0.75rem;
    padding: 0.25rem 0.4rem;
    gap: 0.15rem;
    
    span {
      font-size: 0.6rem;
    }
    
    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

const Section = styled.section`
  padding: 6rem 0;
  position: relative;

  &:nth-child(even) {
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  }

  &:nth-child(odd) {
    background: white;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 1rem;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: ${brandTheme.colors.primary[900]};
  line-height: 1.2;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[500]} 0%, ${brandTheme.colors.secondary[500]} 100%);
    border-radius: 2px;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 1px solid #e5e7eb;

  .icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    color: white;
  }

  h3 {
    margin-bottom: 1rem;
    color: #1a1a1a;
  }

  p {
    color: #666;
  }
`;

const BenefitsList = styled.div`
  ul {
    list-style: none;
    padding: 0;
  }

  li {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    padding: 1rem;
    background: #f8fafc;
    border-radius: 10px;
    border-left: 4px solid #FECE1B;

    svg {
      color: #10b981;
      flex-shrink: 0;
    }
  }
`;

const ProcessSection = styled.section`
  background: #f8fafc;
  padding: 5rem 0;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
`;

const ProcessStep = styled(motion.div)`
  text-align: center;
  padding: 1.5rem;

  .step-number {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 1.2rem;
    margin: 0 auto 1rem;
  }

  h3 {
    margin-bottom: 0.5rem;
    color: #1a1a1a;
  }

  p {
    color: #666;
    font-size: 0.9rem;
  }
`;

const ApplicationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ApplicationCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;

  h3 {
    color: #FECE1B;
    margin-bottom: 1rem;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
      color: #555;

      svg {
        color: #10b981;
        flex-shrink: 0;
      }
    }
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  color: ${brandTheme.colors.neutral.gray[600]};
  max-width: 600px;
  margin: 0 auto 3rem auto;
  line-height: 1.6;
`;

const FAQSection = styled.section`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 6rem 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23e2e8f0' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled(motion.div)`
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }

  .question {
    padding: 1.5rem 2rem;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 600;
    font-size: 1.1rem;
    color: ${brandTheme.colors.primary[900]};
    transition: all 0.3s ease;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);

    &:hover {
      background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    }

    svg {
      transition: transform 0.3s ease;
      color: ${brandTheme.colors.primary[500]};
    }

    &.active svg {
      transform: rotate(180deg);
    }
  }

  .answer {
    padding: 0 2rem;
    max-height: 0;
    overflow: hidden;
    transition: all 0.3s ease;
    background: white;
    color: ${brandTheme.colors.neutral.gray[700]};
    line-height: 1.6;

    &.active {
      padding: 0 2rem 1.5rem 2rem;
      max-height: 200px;
    }
  }

  @media (max-width: 768px) {
    .question {
      padding: 1.25rem 1.5rem;
      font-size: 1rem;
    }

    .answer {
      &.active {
        padding: 0 1.5rem 1.25rem 1.5rem;
      }
    }
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  color: white;
  padding: 6rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const PathwaysPage: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: 'How long does a resin pathway installation take?',
      answer: 'Most residential resin pathways are completed within 1 day. The exact timeframe depends on the length and complexity of the pathway, as well as any additional preparation work required. We provide a detailed timeline during your free consultation.'
    },
    {
      question: 'Are resin pathways slip-resistant?',
      answer: 'Yes, our resin bound pathways are designed to be slip-resistant, even in wet conditions. The textured surface provides excellent grip, making them safe for walking in all weather conditions, including rain and frost.'
    },
    {
      question: 'Can resin pathways be curved or follow complex routes?',
      answer: 'Absolutely! One of the major advantages of resin bound surfacing is its flexibility. We can create curved pathways, follow complex garden layouts, and even create stepping stone effects or custom patterns to match your garden design.'
    },
    {
      question: 'What maintenance do resin pathways require?',
      answer: 'Resin pathways are very low maintenance. Regular cleaning with a pressure washer or garden hose is usually sufficient. We recommend annual inspection and occasional re-sealing every 3-5 years to maintain optimal performance and appearance.'
    },
    {
      question: 'How much does a resin pathway cost?',
      answer: 'Resin pathway costs typically range from £40-60 per square metre, depending on the complexity, chosen materials, and any special design requirements. We provide free, no-obligation quotes with detailed breakdowns of all costs involved.'
    },
    {
      question: 'Can resin pathways be installed in winter?',
      answer: 'Yes, resin bound surfacing can be installed in most weather conditions, including winter months. However, we avoid installation during heavy rain, snow, or when temperatures drop below 5°C to ensure optimal curing and finish quality.'
    }
  ];

  const features = [
    {
      icon: <Footprints size={30} />,
      title: 'Slip-Resistant',
      description: 'Safe walking surface even in wet conditions'
    },
    {
      icon: <Shield size={30} />,
      title: 'Weather Resistant',
      description: 'Withstands UK weather conditions year-round'
    },
    {
      icon: <Clock size={30} />,
      title: 'Quick Installation',
      description: 'Most pathways completed in 1 day'
    },
    {
      icon: <Palette size={30} />,
      title: 'Design Flexibility',
      description: 'Curved paths, patterns, and custom designs'
    }
  ];

  const benefits = [
    'Slip-resistant surface for safety',
    'Low maintenance - just occasional cleaning',
    'No weeds or moss growth',
    'Weather resistant and durable',
    'Customisable colours and patterns',
    'Suitable for all garden styles',
    'Environmentally friendly materials',
    'Professional installation guaranteed'
  ];

  const processSteps = [
    {
      number: 1,
      title: 'Design Consultation',
      description: 'Discuss your patio, paving or pathway layout, route and aesthetic preferences'
    },
    {
      number: 2,
      title: 'Site Preparation',
      description: 'Mark out the patio or pathway and prepare the base for resin application'
    },
    {
      number: 3,
      title: 'Resin Application',
      description: 'Apply the resin bound mixture with your chosen aggregate and colour'
    },
    {
      number: 4,
      title: 'Final Touches',
      description: 'Clean up, seal edges, and provide maintenance instructions'
    }
  ];

  const applications = [
    {
      title: 'Garden Patios & Pathways',
      features: [
        'Curved garden paths',
        'Stepping stone effects',
        'Natural stone colours',
        'Border and edging options'
      ]
    },
    {
      title: 'Side Access Paths',
      features: [
        'Narrow side passages',
        'Utility access routes',
        'Wheelchair accessible',
        'Durable for regular use'
      ]
    },
    {
      title: 'Pool Surrounds',
      features: [
        'Slip-resistant finish',
        'Quick-drying surface',
        'Pool-friendly materials',
        'Easy to clean and maintain'
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Resin Patios & Paving London | Seamless Patios, Courtyards & Pathways</title>
        <meta name="description" content="Design-led resin bound patios, courtyards and pathways across London, Surrey & Hertfordshire. Seamless paving, slip-resistant finishes and custom aggregates. Free quotes & 10+ years experience." />
        <meta name="keywords" content="resin patios London, resin paving London, resin pathways London, resin courtyards London, resin terrace London, resin paving Westminster, resin patios Camden, resin pathways Islington, resin paving Hackney, resin patios Richmond, resin paving Kingston, resin pathways Croydon, resin patios Bromley, resin paving Bexley, resin patios Havering, resin patios Hertfordshire, resin paving Surrey, resin patio installers London, resin paving contractors London, permeable patios London, anti-slip paving London, resin patio quotes London" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/resin-pathways-london/" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Resin Patios & Paving Installation",
            "description": "Resin bound patio, courtyard and pathway installation service in London",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Resin Surfaces London",
              "url": "https://www.resinsurfaceslondon.co.uk"
            },
            "areaServed": ["London", "Surrey", "Hertfordshire"],
            "serviceType": "Resin Bound Patios & Paving"
          })}
        </script>
      </Helmet>

      <HeroSectionComponent
        title="Resin Patios, Courtyards & Paving London"
        subtitle="Upgrade patios, courtyards, paths and side access routes with seamless, slip-resistant resin bound surfacing that stays cool, clean and puddle-free."
        badge="Patios • Courtyards • Pathways"
        backgroundImage="/images/resin-surfaces-bound-work8.jpeg"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />

      <Section>
        <Container>
          <TwoColumnGrid>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img src="/images/resin-surfaces-bound-work8.jpeg" alt="Resin bound pathway London" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Beautiful Patios, Courtyards & Pathways</h2>
              <p>
                Resin bound patios and paving are the perfect solution for modern gardens. Our slip-resistant 
                surfacing creates smooth entertaining areas while maintaining the natural beauty of your outdoor space.
              </p>
              <p>
                Whether you need a seamless courtyard, a family patio, side access route or pool surround, our resin 
                bound paving is built to last. With custom colours and patterns, we tailor every surface to complement your property.
              </p>
              <BenefitsList>
                <ul>
                  {benefits.slice(0, 4).map((benefit, index) => (
                    <li key={index}>
                      <CheckCircle size={20} />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </BenefitsList>
            </motion.div>
          </TwoColumnGrid>

          <SectionTitle>Key Features & Benefits</SectionTitle>
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </FeatureCard>
            ))}
          </FeaturesGrid>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Complete Benefits List</h2>
            <BenefitsList>
              <ul>
                {benefits.map((benefit, index) => (
                  <li key={index}>
                    <CheckCircle size={20} />
                    {benefit}
                  </li>
                ))}
              </ul>
            </BenefitsList>
          </motion.div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionTitle>Patio & Paving Applications</SectionTitle>
          <ApplicationsGrid>
            {applications.map((application, index) => (
              <ApplicationCard
                key={application.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h3>{application.title}</h3>
                <ul>
                  {application.features.map((feature, i) => (
                    <li key={i}>
                      <CheckCircle size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </ApplicationCard>
            ))}
          </ApplicationsGrid>
        </Container>
      </Section>

      <ProcessSection>
        <Container>
          <SectionTitle>Our Installation Process</SectionTitle>
          <ProcessGrid>
            {processSteps.map((step, index) => (
              <ProcessStep
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </ProcessStep>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <FAQSection>
        <Container>
          <SectionTitle>Frequently Asked Questions About Resin Patios & Paving</SectionTitle>
          <SectionSubtitle>
            Get answers to common questions about our resin pathway installation, design options, and maintenance
          </SectionSubtitle>
          <FAQGrid>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                className={openFAQ === index ? 'active' : ''}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div 
                  className={`question ${openFAQ === index ? 'active' : ''}`}
                  onClick={() => toggleFAQ(index)}
                >
                  {faq.question}
                  <ChevronDown size={20} />
                </div>
                <div className={`answer ${openFAQ === index ? 'active' : ''}`}>
                  {faq.answer}
                </div>
              </FAQItem>
            ))}
          </FAQGrid>
        </Container>
      </FAQSection>

      <CTASection>
        <Container>
          <SectionTitle style={{ color: 'white' }}>Ready for Your New Patio or Pathway?</SectionTitle>
          <p style={{ fontSize: '1.2rem', marginBottom: '2rem', opacity: 0.9 }}>
            Create beautiful, safe pathways that enhance your garden and property
          </p>
          <CTAButtons>
            <StyledButton to="/contact/" className="secondary">
              Get Free Quote <ArrowRight size={20} />
            </StyledButton>
            <StyledButton to="https://wa.me/447535935779" className="secondary">
              <MessageCircle size={20} /> WhatsApp Chat
            </StyledButton>
          </CTAButtons>
        </Container>
      </CTASection>
    </>
  );
};

export default PathwaysPage;
